'use client';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { withViewTransition } from '../animations/viewTransition';

const I18nContext = createContext(null);

function getByPath(obj, path) {
  return path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj);
}

/**
 * Provider i18n framework-agnostic (nessuna dipendenza da router) — pensato
 * per siti one-page o comunque non basati su route per-lingua. Persiste la
 * lingua scelta in localStorage. I dizionari sono JSON statici per lingua,
 * generati/aggiornati con scripts/i18n/translate-dictionary.js.
 *
 * @param {{
 *   dictionaries: Record<string, object>,   // { it: {...}, en: {...}, ... }
 *   locales: Array<{ code: string, label: string, flag?: string }>,
 *   defaultLocale?: string,
 *   storageKey?: string,
 *   children: React.ReactNode,
 * }} props
 */
export function I18nProvider({ dictionaries, locales, defaultLocale = 'it', storageKey = 'vs-locale', children }) {
  const [locale, setLocaleState] = useState(defaultLocale);

  useEffect(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
    if (saved && dictionaries[saved]) setLocaleState(saved);
  }, [storageKey, dictionaries]);

  const setLocale = useCallback((code) => {
    if (!dictionaries[code]) return;
    // Crossfade nativo del browser tra testo prima/dopo il cambio lingua, se supportato.
    withViewTransition(() => setLocaleState(code));
    if (typeof window !== 'undefined') window.localStorage.setItem(storageKey, code);
  }, [dictionaries, storageKey]);

  const t = useCallback((key, vars) => {
    const dict = dictionaries[locale] || {};
    const fallback = dictionaries[defaultLocale] || {};
    const raw = getByPath(dict, key) ?? getByPath(fallback, key) ?? key;
    if (typeof raw !== 'string' || !vars) return raw;
    return raw.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{{${k}}}`));
  }, [dictionaries, locale, defaultLocale]);

  const value = useMemo(() => ({ locale, setLocale, t, locales }), [locale, setLocale, t, locales]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

/** @returns {{ locale: string, setLocale: (code: string) => void, t: (key: string, vars?: object) => string, locales: Array<object> }} */
export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n va usato dentro <I18nProvider>');
  return ctx;
}
